import {
  doc,
  getDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../shared/storage/firebase';
import { DEFAULT_TZ, formatTimeInZone } from '../../../shared/utils/timezone';
import type { AvailabilityQuarterKey } from '../../../types';

export type SubStatus = 'none' | 'requested' | 'assigned' | 'canceled';
export type SubRequestStatus = 'open' | 'claimed' | 'canceled';

export type SubRequestReason =
  | 'schedule_conflict'
  | 'illness'
  | 'travel'
  | 'other';

export type RequestSubForSquadEventParams = {
  eventId: string;
  squadId: string;
  requestedByTeacherId: string;
  /** Required only if the same teacher occupies both lead and co-teacher roles. */
  requestedRole?: 'lead' | 'co_teacher';
  classTitle: string;
  classStart: Date;
  classEnd: Date;
  quarterKey: AvailabilityQuarterKey;
  dateKey: string; // YYYY-MM-DD in availabilityTimeZone
  reason: SubRequestReason;
  note: string;
  /**
   * Optional: if provided, will be stored on the sub request for notification targeting.
   * If omitted, the server may compute eligible teachers when sending notifications.
   */
  eligibleTeacherIds?: string[];
  /**
   * Timezone context for interpreting dateKey + availability time-of-day.
   * Defaults to platform timezone if omitted.
   */
  availabilityTimeZone?: string;
  /**
   * Optional: if the request was triggered from an availability exception editor,
   * include the drafted range so we can preserve the intended override while still
   * enforcing unavailability during the class time (v1: single range per dateKey).
   */
  requestedAvailabilityRangeLocal?: { start: string; end: string };
};

export type ClaimSubOpportunityParams = {
  eventId: string;
  squadId: string;
  claimerTeacherId: string;
  /** Admin only: fill the request with this teacher instead of the caller. V2 requests only. */
  substituteTeacherId?: string;
};

export type CancelSubRequestParams = {
  eventId: string;
  squadId: string;
  requestedByTeacherId: string;
};

export type RestoreOriginalTeacherParams = {
  eventId: string;
  squadId: string;
};

function isV2SubRequest(data: any): boolean {
  return Number(data?.lifecycleVersion) === 2
    && (data?.requestedRole === 'lead' || data?.requestedRole === 'co_teacher')
    && typeof data?.requestedTeacherId === 'string'
    && Boolean(data.requestedTeacherId.trim());
}

function hasAnyV2Marker(event: any, request: any): boolean {
  return (Array.isArray(event?.activeCoverage) && event.activeCoverage.length > 0)
    || Number(request?.lifecycleVersion) === 2
    || Boolean(request?.requestedRole)
    || Boolean(request?.requestedTeacherId);
}

export function quarterKeyForDateCt(d: Date): AvailabilityQuarterKey {
  const y = Number(formatTimeInZone(d, DEFAULT_TZ, 'yyyy'));
  const m = Number(formatTimeInZone(d, DEFAULT_TZ, 'M')); // 1-12
  if (y === 2026 && m >= 4 && m <= 6) return 'spring2026';
  if (y === 2026 && m >= 7 && m <= 8) return 'summer2026';
  if (y === 2026 && m >= 9 && m <= 12) return 'fall2026';
  if (y === 2027 && m >= 1 && m <= 3) return 'winter2027';
  if (y === 2027 && m >= 4 && m <= 6) return 'spring2027';
  return 'winter2026';
}

export function dateKeyForDateCt(d: Date): string {
  return formatTimeInZone(d, DEFAULT_TZ, 'yyyy-MM-dd');
}

export function quarterKeyForDateInZone(d: Date, timeZone?: string | null): AvailabilityQuarterKey {
  const tz = String(timeZone || DEFAULT_TZ).trim() || DEFAULT_TZ;
  const y = Number(formatTimeInZone(d, tz, 'yyyy'));
  const m = Number(formatTimeInZone(d, tz, 'M')); // 1-12
  if (y === 2026 && m >= 4 && m <= 6) return 'spring2026';
  if (y === 2026 && m >= 7 && m <= 8) return 'summer2026';
  if (y === 2026 && m >= 9 && m <= 12) return 'fall2026';
  if (y === 2027 && m >= 1 && m <= 3) return 'winter2027';
  if (y === 2027 && m >= 4 && m <= 6) return 'spring2027';
  return 'winter2026';
}

export function dateKeyForDateInZone(d: Date, timeZone?: string | null): string {
  const tz = String(timeZone || DEFAULT_TZ).trim() || DEFAULT_TZ;
  return formatTimeInZone(d, tz, 'yyyy-MM-dd');
}

export async function requestSubForSquadEvent(params: RequestSubForSquadEventParams): Promise<{ subRequestId: string }> {
  if (!params.eventId || !params.squadId || !params.requestedByTeacherId) throw new Error('Missing required fields.');
  if (!String(params.note || '').trim()) throw new Error('Teaching notes are required to request a sub.');
  const fn = httpsCallable(getFunctions(), 'requestSubOpportunityV2');
  const { classStart, classEnd, ...serializable } = params;
  const result: any = await fn({
    ...serializable,
    classStartMs: classStart.getTime(),
    classEndMs: classEnd.getTime(),
  });
  return { subRequestId: String(result?.data?.subRequestId || '') };
}

// Load the event + its request to decide which lifecycle handles this action.
// Read failures (rules denial, flaky network) surface as a clear message instead
// of an opaque crash before the callable is ever reached.
async function loadSubRoutingContext(eventId: string): Promise<{ event: any; request: any; requestId: string }> {
  try {
    const evSnap = await getDoc(doc(db, 'squadEvents', eventId));
    const event = evSnap.exists() ? evSnap.data() : null;
    const requestId = event ? String((event as any)?.subRequestId || '') : '';
    const requestSnap = requestId ? await getDoc(doc(db, 'subRequests', requestId)) : null;
    const request = requestSnap?.exists() ? requestSnap.data() : null;
    return { event, request, requestId };
  } catch (err) {
    console.error('Failed to load sub request routing context', err);
    throw new Error('Could not load this sub request. Check your connection and try again.');
  }
}

export async function claimSubOpportunity(params: ClaimSubOpportunityParams): Promise<void> {
  const { eventId, squadId, claimerTeacherId, substituteTeacherId } = params;
  if (!eventId || !squadId || !claimerTeacherId) throw new Error('Missing required fields.');

  const { event, request, requestId } = await loadSubRoutingContext(eventId);
  const useV2 = isV2SubRequest(request) || hasAnyV2Marker(event, request);
  if (useV2 && !requestId) {
    throw new Error('This class has coverage state but no open sub request. Ask an admin to resolve it.');
  }
  const fn = httpsCallable(getFunctions(), useV2 ? 'claimSubOpportunityV2' : 'claimSubOpportunity');
  await fn({ eventId, squadId, subRequestId: requestId, ...(substituteTeacherId ? { substituteTeacherId } : {}) });
}

export async function cancelSubRequestForSquadEvent(params: CancelSubRequestParams): Promise<void> {
  const { eventId, squadId, requestedByTeacherId } = params;
  if (!eventId || !squadId || !requestedByTeacherId) throw new Error('Missing required fields.');

  const { event, request, requestId } = await loadSubRoutingContext(eventId);
  if (isV2SubRequest(request) || hasAnyV2Marker(event, request)) {
    if (!requestId) {
      throw new Error('This class has coverage state but no open sub request. Ask an admin to resolve it.');
    }
    const fn = httpsCallable(getFunctions(), 'cancelSubOpportunityV2');
    await fn({ eventId, squadId, subRequestId: requestId });
    return;
  }

  // Temporary legacy cancellation path for requests created before V2 deploy.
  const legacy = httpsCallable(getFunctions(), 'cancelSubOpportunityLegacy');
  await legacy({ eventId, squadId, subRequestId: requestId, requestedByTeacherId });
}

export async function restoreOriginalTeacherForSquadEvent(params: RestoreOriginalTeacherParams): Promise<void> {
  if (!params.eventId || !params.squadId) throw new Error('Missing required fields.');
  const fn = httpsCallable(getFunctions(), 'adminRestoreOriginalTeacher');
  await fn(params);
}
